import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import { getNewsList, updateArticle } from '../api';
import ArticleIcon, { NEWS_ICON_FALLBACK } from '../components/ArticleIcon';
import useAuth from '../auth/useAuth';
import Button from '../components/ui/Button';
import IconButton from '../components/ui/IconButton';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import ArticleLink from '../components/ArticleLink';

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  t,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function toggleOption(option) {
    if (selected.includes(option)) {
      onChange(selected.filter((item) => item !== option));
    } else {
      onChange([...selected, option]);
    }
  }

  return (
    <div
      className="account-choice"
      ref={dropdownRef}
      style={{ position: 'relative' }}
    >
      <button
        type="button"
        className="ui-button account-secondary-button"
        onClick={() => setIsOpen((value) => !value)}
        aria-expanded={isOpen}
      >
        <span>
          {selected.length > 0
            ? `${label} (${selected.length})`
            : label}
        </span>

        <i
          className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 5px)',
            left: 0,
            zIndex: 10000,
            width: '100%',
            minWidth: '220px',
            maxHeight: '260px',
            overflowY: 'auto',
            padding: '8px',
            boxSizing: 'border-box',
            background: 'var(--background-color, #fff)',
            color: 'var(--text-color, #222)',
            border: '1px solid var(--border-color, #ccc)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '1px 5px',
                  cursor: 'pointer',
                  borderRadius: '5px',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleOption(option)}
                  style={{
                    flexShrink: 0,
                    width: '16px',
                    height: '16px',
                    margin: 0,
                  }}
                />

                <span>{option}</span>
              </label>
            ))
          ) : (
            <p style={{ padding: '8px', margin: 0 }}>
              {t('articlesPage.noOptions')}
            </p>
          )}

          {selected.length > 0 && (
            <button
              type="button"
              className="ui-button account-secondary-button"
              onClick={() => onChange([])}
              style={{
                width: '100%',
                marginTop: '6px',
              }}
            >
              {t('articlesPage.clearSelection')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatNewsDate(seconds, locale) {
  if (!seconds) return '';

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(seconds * 1000));
}

function openDatePicker(id) {
  const input = document.getElementById(id);

  if (input?.showPicker) {
    input.showPicker();
  } else {
    input?.focus();
  }
}

function News() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const canManage = user?.rightsAnywhere?.includes('news.manage') ?? false;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState('default');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadNews() {
      try {
        setLoading(true);
        setError(null);

        const data = await getNewsList();

        if (!cancelled) {
          setItems(
            (data ?? []).filter(
              (item) => !item.is_hidden || item.canChangeVisibility,
            ),
          );
        }
      } catch (err) {
        console.error('Failed to load news:', err);

        if (!cancelled) {
          setError(t('newsPage.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadNews();

    return () => {
      cancelled = true;
    };
  }, [t]);

  async function handleToggleVisibility(item) {
    try {
      setError(null);

      const updated = await updateArticle(item.article_id, {
        is_hidden: !item.is_hidden,
      });

      setItems((current) =>
        current.map((entry) =>
          entry.article_id === item.article_id
            ? { ...entry, is_hidden: updated.is_hidden }
            : entry,
        ),
      );
    } catch (err) {
      console.error('Failed to change news visibility:', err);
      setError(t('articlesPage.visibilityError'));
    }
  }

  const tags = useMemo(() => {
    const allTags = items.flatMap((item) =>
      (item.tags ?? []).map((tag) =>
        typeof tag === 'string' ? tag : tag.name,
      ),
    );

    return [...new Set(allTags.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, i18n.language),
    );
  }, [items, i18n.language]);

  const authors = useMemo(() => {
    const allAuthors = items
      .map((item) => item.author?.name || item.author?.nickname)
      .filter(Boolean);

    return [...new Set(allAuthors)].sort((a, b) =>
      a.localeCompare(b, i18n.language),
    );
  }, [items, i18n.language]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const itemTags = (item.tags ?? []).map((tag) =>
        typeof tag === 'string' ? tag : tag.name,
      );

      if (
        selectedTags.length > 0 &&
        !selectedTags.some((tag) => itemTags.includes(tag))
      ) {
        return false;
      }

      const author = item.author?.name || item.author?.nickname || '';

      if (
        selectedAuthors.length > 0 &&
        !selectedAuthors.includes(author)
      ) {
        return false;
      }

      const timestamp = item.creation_date
        ? new Date(item.creation_date * 1000)
        : null;

      if (dateFrom) {
        if (!timestamp || timestamp < new Date(`${dateFrom}T00:00:00`)) {
          return false;
        }
      }

      if (dateTo) {
        if (!timestamp || timestamp > new Date(`${dateTo}T23:59:59.999`)) {
          return false;
        }
      }

      return true;
    });
  }, [
    items,
    selectedTags,
    selectedAuthors,
    dateFrom,
    dateTo,
  ]);

  const displayedItems = useMemo(() => {
    const result = [...filteredItems];

    if (sortBy === 'title') {
      result.sort((a, b) =>
        (a.title ?? '').localeCompare(b.title ?? '', i18n.language),
      );
    } else if (sortBy === 'author') {
      result.sort((a, b) => {
        const authorA = a.author?.name || a.author?.nickname || '';
        const authorB = b.author?.name || b.author?.nickname || '';

        return authorA.localeCompare(authorB, i18n.language);
      });
    } else if (sortBy === 'date') {
      result.sort(
        (a, b) => (a.creation_date ?? 0) - (b.creation_date ?? 0),
      );
    }

    if (sortDirection === 'desc' && sortBy !== 'default') {
      result.reverse();
    }

    return result;
  }, [filteredItems, sortBy, sortDirection, i18n.language]);

  function resetFilters() {
    setSortBy('default');
    setSortDirection('asc');
    setSelectedTags([]);
    setSelectedAuthors([]);
    setDateFrom('');
    setDateTo('');
  }

  return (
    <PageLayout showSearch showAiSearch>
      <PageMain className="articles-page-main">
        <PageHeader
          title={t('common.companyNews')}
          condensed
          note={!loading && items.length === 0 ? t('common.emptyNote') : null}
          actions={
            canManage && (
              <Button to="/create-news" variant="primary">
                <i className="fa-solid fa-plus" />
                {t('newsPage.createButtonLabel')}
              </Button>
            )
          }
        />

        {/* Sorting and filters */}
        {!loading && !error && items.length > 0 && (
          <div className="article-controls article-controls--articles-page">
            <div className="article-controls__top">
              <div className="account-choice article-sort-wrapper">
                <button
                  type="button"
                  className="ui-button account-secondary-button"
                  onClick={() =>
                    document.getElementById('news-sort-select').click()
                  }
                >
                  <span>
                    {sortBy === 'default'
                      ? t('articlesPage.sortDefault')
                      : sortBy === 'title'
                        ? t('articlesPage.sortTitle')
                        : sortBy === 'author'
                          ? t('articlesPage.filterByAuthor')
                          : t('articlesPage.sortDate')}
                  </span>

                  <i
                    className="fa-solid fa-chevron-down"
                    aria-hidden="true"
                  />
                </button>

                <select
                  id="news-sort-select"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  aria-label={t('articlesPage.sortLabel')}
                >
                  <option value="default">
                    {t('articlesPage.sortDefault')}
                  </option>
                  <option value="title">
                    {t('articlesPage.sortTitle')}
                  </option>
                  <option value="date">
                    {t('articlesPage.sortDate')}
                  </option>
                </select>
              </div>

              <button
                type="button"
                className="ui-button account-secondary-button"
                onClick={() =>
                  setSortDirection((direction) =>
                    direction === 'asc' ? 'desc' : 'asc',
                  )
                }
                aria-label={t(
                  sortDirection === 'asc'
                    ? 'articlesPage.sortAscending'
                    : 'articlesPage.sortDescending',
                )}
                disabled={sortBy === 'default'}
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>

              <button
                type="button"
                className="ui-button account-secondary-button"
                onClick={() => setShowFilters((value) => !value)}
                aria-expanded={showFilters}
              >
                <i className="fa-solid fa-filter" aria-hidden="true" />
                {showFilters
                  ? t('articlesPage.hideFilters')
                  : t('articlesPage.showFilters')}
              </button>

              <button
                type="button"
                className="ui-button account-secondary-button"
                onClick={resetFilters}
              >
                <i className="fa-solid fa-rotate-left" aria-hidden="true" />
                {t('articlesPage.resetFilters')}
              </button>
            </div>

            {showFilters && (
              <div
                className="article-controls__filters"
                style={{ overflow: 'visible' }}
              >
                <MultiSelectDropdown
                  label={t('articlesPage.filterByTag')}
                  options={tags}
                  selected={selectedTags}
                  onChange={setSelectedTags}
                  t={t}
                />

                <MultiSelectDropdown
                  label={t('articlesPage.filterByAuthor')}
                  options={authors}
                  selected={selectedAuthors}
                  onChange={setSelectedAuthors}
                  t={t}
                />

                <div className="ui-button account-secondary-button">
                  <label htmlFor="news-date-from">
                    {t('articlesPage.dateFrom')}
                  </label>

                  <div className="article-date-filter__control">
                    <input
                      id="news-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(event) => setDateFrom(event.target.value)}
                      className="article-date-filter__input"
                    />

                    <button
                      type="button"
                      className="article-date-filter__button"
                      onClick={() => openDatePicker('news-date-from')}
                      aria-label={t('articlesPage.dateFrom')}
                    >
                      <i
                        className="fa-regular fa-calendar"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>

                <div className="ui-button account-secondary-button">
                  <label htmlFor="news-date-to">
                    {t('articlesPage.dateTo')}
                  </label>

                  <div className="article-date-filter__control">
                    <input
                      id="news-date-to"
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(event) => setDateTo(event.target.value)}
                      className="article-date-filter__input"
                    />

                    <button
                      type="button"
                      className="article-date-filter__button"
                      onClick={() => openDatePicker('news-date-to')}
                      aria-label={t('articlesPage.dateTo')}
                    >
                      <i
                        className="fa-regular fa-calendar"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <PageBody>
            <p className="page-message">{error}</p>
          </PageBody>
        )}

        {loading ? (
          <PageBody>
            <p className="page-message">{t('newsPage.loading')}</p>
          </PageBody>
        ) : items.length === 0 ? null : displayedItems.length === 0 ? (
          <PageBody>
            <p className="page-message">
              {t('articlesPage.noMatchingArticles')}
            </p>
          </PageBody>
        ) : (
          <PageBody className="related-news articles-list">
            <div className="related-grid news-grid">
              {displayedItems.map((item) => (
                <div
                  key={item.article_id}
                  className={`related-card news-list-card${
                    item.is_hidden ? ' news-list-card--hidden' : ''
                  }`}
                >
                  <ArticleLink
                    article={item}
                    className="news-list-card-link"
                  >
                    <div className="news-list-card-icon">
                      <ArticleIcon
                        icon={item.icon}
                        fallback={NEWS_ICON_FALLBACK}
                        seed={item.title}
                        alt=""
                      />
                    </div>

                    <h4>{item.title}</h4>
                    <p>{item.summary}</p>

                    <span>
                      {formatNewsDate(item.creation_date, i18n.language)}
                      {item.is_hidden &&
                        ` · ${t('newsPage.hiddenLabel')}`}
                    </span>
                  </ArticleLink>

                  <div className="article-actions">
                    {item.canChangeVisibility && (
                      <IconButton
                        className="article-visibility-button news-list-card-visibility"
                        icon={
                          item.is_hidden
                            ? 'fa-regular fa-eye-slash'
                            : 'fa-regular fa-eye'
                        }
                        label={
                          item.is_hidden
                            ? t('newsPage.showNews')
                            : t('newsPage.hideNews')
                        }
                        onClick={() => handleToggleVisibility(item)}
                      />
                    )}

                    {item.canChangeVisibility && (
                      <IconButton
                        className={`article-visibility-button${
                          item.has_history
                            ? ''
                            : ' article-history-disabled'
                        }`}
                        icon="fa-solid fa-clock-rotate-left"
                        label={
                          item.has_history
                            ? t('articlesPage.showFromArchive')
                            : t('articlesPage.noHistory')
                        }
                        title={
                          item.has_history
                            ? t('articlesPage.showFromArchive')
                            : t('articlesPage.noHistory')
                        }
                        to={
                          item.has_history
                            ? `/archive/${item.article_id}/history`
                            : undefined
                        }
                        disabled={!item.has_history}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </PageBody>
        )}
      </PageMain>
    </PageLayout>
  );
}

export default News;